/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {SideNavIssuesIcon} from '../../../icons/current/SideNavIssuesIcon';
import {dlsColor, dlsTextColor} from '../../../theme/theme';
import {IconSize} from '../../../types/types';

export interface FaultCodesCountBadgeProps {
  count: number;
}

const useStyles = makeStyles(() => ({
  badge : {
    boxSizing     : 'border-box',
    display       : 'flex',
    flexDirection : 'column',
    alignItems    : 'flex-end',
    gap           : '8px'
  },
  data : {
    display    : 'flex',
    alignItems : 'center',
    gap        : '8px'
  },
  count : {
    fontFamily : 'Inter',
    fontWeight : 500,
    fontSize   : '18px',
    lineHeight : '22px',
    color      : dlsTextColor('red')
  },
  label : {
    fontFamily    : 'Roboto',
    fontWeight    : 700,
    fontSize      : '10px',
    lineHeight    : '12px',
    letterSpacing : '1px',
    textTransform : 'uppercase',
    textAlign     : 'right',
    color         : dlsTextColor('red')
  }
}));

/**
 * FaultCodesCountBadge renders the ION-7462 Fault Codes section's active
 * fault count - a siren icon and numeric count, with an "ACTIVE FAULTS"
 * label beneath. Per the Figma "Fault Codes Container" annotation, this
 * badge is always red regardless of count - there is no zero/inactive
 * state, since the whole `FaultCodesSection` is omitted instead when there
 * are no fault codes.
 *
 * ```typescript
 *
 *   import {FaultCodesCountBadge} from
 *     '@ion/dls/src/components/assetdetails/current/FaultCodesCountBadge';
 *   ...
 *
 *   <FaultCodesCountBadge count={20} />
 *
 * ```
 *
 */
export const FaultCodesCountBadge = ({count}: FaultCodesCountBadgeProps) => {
  const classes = useStyles();
  return (
    <div className={classes.badge}>
      <div className={classes.data}>
        <SideNavIssuesIcon size={IconSize.medium} color={dlsColor('red')} />
        <span className={classes.count}>
          {count}
        </span>
      </div>
      <span className={classes.label}>
        ACTIVE FAULTS
      </span>
    </div>
  );
};

export default memo(FaultCodesCountBadge);
